import React, { useState, useEffect } from 'react';
import './InstructionsModal.css';
import '../styles/ActionButtons.css';
import TermsAndConditionsModal from './TermsAndConditionsModal';
import { SESSION_APP_DATA_BASE } from '../config/sessionAppData';
import { getApiBaseUrl, SALON_API_PREFIX } from '../utils/apiConfig';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';

const DEFAULT_NOTICE_HTML = (
  <>
    <p>
      Nyafaz uses specialized AI to assess your skin health. To enable
      this, we perform a facial scan that involves temporary processing
      of your facial characteristic data, such as facial geometry.
    </p>
    <p>
      Your data is processed securely via Nyafaz's cloud infrastructure.
      This information is retained to enable you to track your progress and perform
      future comparisons of your skin analysis results. Your personal Data
      and results are strictly confidential and never shared with third parties.
    </p>
  </>
);

function InstructionsModal({ isOpen, onClose, onContinue, token }) {
  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [noticeContent, setNoticeContent] = useState(null);
  const [noticeLoading, setNoticeLoading] = useState(false);
  const [disclaimerModal, setDisclaimerModal] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const apiBase = getApiBaseUrl();
    const url = apiBase
      ? `${apiBase.replace(/\/$/, '')}${SALON_API_PREFIX}/config/disclaimer`
      : `${SALON_API_PREFIX}/config/disclaimer`;
    fetchWithTimeout(url, { timeoutMs: 15000 })
      .then((res) => res.ok ? res.json() : Promise.reject())
      .then((data) => {
        if (!cancelled && data && typeof data.disclaimer_modal === 'string') {
          setDisclaimerModal(data.disclaimer_modal.trim());
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !token) {
      setNoticeContent(null);
      return;
    }
    let cancelled = false;
    setNoticeLoading(true);
    fetchWithTimeout(`${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/capture`, { timeoutMs: 20000 })
      .then((res) => {
        if (!res.ok) throw new Error('Capture content unavailable');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const html = data?.text_area ?? data?.data?.text_area ?? null;
        setNoticeContent(typeof html === 'string' && html.trim() ? html : null);
      })
      .catch(() => {
        if (!cancelled) setNoticeContent(null);
      })
      .finally(() => {
        if (!cancelled) setNoticeLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, token]);

  if (!isOpen) return null;

  const instructions = [
    {
      id: 'glasses',
      icon: (
        <svg fill="currentColor" className="instruction-svg" viewBox="-50 -50 612 612">
          <g>
            <path d="M278.757,249.941c-7.248-2.052-14.931-3.129-22.649-3.129c-7.974,0-15.699,1.155-23.165,3.343 c0.011,0.224,0.131,0.446,0.131,0.672v40.228c0,0.172-0.162,0.34-0.163,0.511c6.903-4.22,14.844-6.547,23.165-6.547 c8.028,0,15.941,2.175,22.676,6.116c0-0.027,0.173-0.054,0.173-0.082v-40.226C278.925,250.528,278.737,250.236,278.757,249.941z" />
            <path d="M215.984,238.764c-3.505-4.253-8.811-6.963-14.75-6.963H65.898l45.354-44.577h17.385c10.551,0,19.104-8.554,19.104-19.104 c0-10.551-8.554-19.104-19.104-19.104h-25.201c-5.009,0-9.82,1.968-13.392,5.479L5.712,237.38C2.058,240.971,0,245.881,0,251.005 v36.292c0,40.529,32.666,73.715,73.543,75.598c1.215,0.056,2.435,0.09,3.664,0.09h67.197c20.364,0,39.443-7.816,53.722-22.008 c14.325-14.238,22.212-33.302,22.212-53.678v-36.393C220.338,246.294,218.704,242.065,215.984,238.764z" />
            <path d="M506.288,237.38l-84.331-82.885c-3.573-3.511-8.383-5.479-13.392-5.479h-25.201c-10.551,0-19.104,8.554-19.104,19.104 c0,10.551,8.554,19.104,19.104,19.104h17.385l45.354,44.577H310.766c-6.032,0-11.404,2.803-14.905,7.171 c-2.622,3.269-4.199,7.415-4.199,11.934v36.391c0,20.377,7.889,39.442,22.212,53.678c14.277,14.193,33.358,22.008,53.722,22.008 h67.197c0.978,0,1.949-0.037,2.919-0.073C478.935,361.398,512,328.07,512,287.296v-36.292 C512,245.881,509.942,240.971,506.288,237.38z" />
          </g>
          {/* Prohibited indicator overlay - Adjusted for better visibility */}
          <g transform="translate(10, 10)">
            <circle cx="460" cy="410" r="60" fill="white" stroke="#ef4444" strokeWidth="15" />
            <line x1="418" y1="368" x2="502" y2="452" stroke="#ef4444" strokeWidth="15" strokeLinecap="round" />
          </g>
        </svg>
      ),
      text: "Remove eyewear and ensure your hair is pulled back to expose your full face"
    },
    {
      id: 'lighting',
      icon: (
        <svg viewBox="0 0 100 100" className="instruction-svg">
          {/* Sun/Light Glow */}
          <circle cx="50" cy="50" r="15" fill="none" stroke="#f59e0b" strokeWidth="3" />
          {/* Rays */}
          {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
            <line
              key={angle}
              x1={50 + 22 * Math.cos((angle * Math.PI) / 180)}
              y1={50 + 22 * Math.sin((angle * Math.PI) / 180)}
              x2={50 + 35 * Math.cos((angle * Math.PI) / 180)}
              y2={50 + 35 * Math.sin((angle * Math.PI) / 180)}
              stroke="#f59e0b"
              strokeWidth="3"
              strokeLinecap="round"
            />
          ))}
        </svg>
      ),
      text: "Position yourself in a bright area for the most accurate skin analysis results"
    }

  ];


  return (
    <div className="instructions-modal-overlay" onClick={onClose}>
      <div className="instructions-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="instructions-modal-header">
          <h2 className="instructions-modal-title">Instructions</h2>
          <button
            className="instructions-close-button"
            onClick={onClose}
            aria-label="Close instructions"
          >
            ×
          </button>
        </div>

        {/* Instructions Content */}
        <div className="instructions-modal-body">
          <div className="instructions-grid">
            {instructions.map((item) => (
              <div key={item.id} className="instruction-item">
                <div className="instruction-icon-box">
                  {item.icon}
                </div>
                <p className="instruction-text">{item.text}</p>
              </div>
            ))}
          </div>

          {/* Notice and Consent Disclaimer */}
          <div className="notice-consent-section">
            <h3 className="notice-consent-title">Privacy and Consent</h3>
            <div className="notice-consent-content">
              {noticeLoading && <p className="notice-consent-loading">Loading...</p>}
              {!noticeLoading && noticeContent && (
                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(noticeContent) }} />
              )}
              {!noticeLoading && !noticeContent && DEFAULT_NOTICE_HTML}
            </div>
          </div>

          {/* Medical Disclaimer (from API) – below Privacy and Consent, no fallback */}
          <div className="instructions-disclaimer-section instructions-disclaimer-api">
            <h3 className="instructions-disclaimer-section-title">Medical Disclaimer</h3>
            {disclaimerModal ? <p className="instructions-disclaimer-api-text">{disclaimerModal}</p> : null}
          </div>
          {/* Disclaimer Checkbox Section */}
          <div className="instructions-disclaimer">
            <div className="disclaimer-checkbox-container">
              <label className="disclaimer-checkbox-label">
                <input
                  type="checkbox"
                  className="disclaimer-checkbox"
                  checked={acceptedDisclaimer}
                  onChange={(e) => setAcceptedDisclaimer(e.target.checked)}
                />
                <span className="disclaimer-checkbox-text">
                  I accept the{' '}
                  <button
                    type="button"
                    className="disclaimer-link-button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowTermsModal(true);
                    }}
                  >
                    Terms & Conditions
                  </button>
                  {' '}and Consent
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="instructions-modal-footer">
          <button
            className="skin-action-button skin-action-button-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className={`skin-action-button ${!acceptedDisclaimer ? 'disabled' : ''}`}
            onClick={onContinue}
            disabled={!acceptedDisclaimer}
          >
            Continue
          </button>
        </div>
      </div>

      {/* Terms and Conditions Modal */}
      <TermsAndConditionsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={() => setAcceptedDisclaimer(true)}
        disclaimerText={disclaimerModal || undefined}
      />
    </div>
  );
}

export default InstructionsModal;


